import { useState } from "react";
import {
  useSearchConfigs,
  useCreateSearchConfig,
  useUpdateSearchConfig,
  useDeleteSearchConfig,
  useSetDefaultConfig,
  useSearchRuns,
  useSearchRunDetail,
  type SearchConfig,
  type SearchRun,
} from "../api/search";
import { formatRelative } from "../components/formatting";

function ConfigPanel() {
  const { data, isLoading } = useSearchConfigs();
  const createMutation = useCreateSearchConfig();
  const updateMutation = useUpdateSearchConfig();
  const deleteMutation = useDeleteSearchConfig();
  const setDefaultMutation = useSetDefaultConfig();

  const [editing, setEditing] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({
    name: "",
    mode: "subagent",
    sources: "reddit",
    reddit_max_threads: 5,
    reddit_max_comments: 15,
    reddit_max_depth: 2,
    web_max_pages: 5,
  });

  function startCreate() {
    setForm({
      name: "",
      mode: "subagent",
      sources: "reddit",
      reddit_max_threads: 5,
      reddit_max_comments: 15,
      reddit_max_depth: 2,
      web_max_pages: 5,
    });
    setCreating(true);
    setEditing(null);
  }

  function startEdit(cfg: SearchConfig) {
    setForm({
      name: cfg.name,
      mode: cfg.mode,
      sources: cfg.sources,
      reddit_max_threads: cfg.reddit_max_threads,
      reddit_max_comments: cfg.reddit_max_comments,
      reddit_max_depth: cfg.reddit_max_depth,
      web_max_pages: cfg.web_max_pages,
    });
    setEditing(cfg.id);
    setCreating(false);
  }

  function cancelEdit() {
    setEditing(null);
    setCreating(false);
  }

  function handleSave() {
    if (creating) {
      createMutation.mutate(form, { onSuccess: () => setCreating(false) });
    } else if (editing) {
      updateMutation.mutate(
        { id: editing, updates: form },
        { onSuccess: () => setEditing(null) }
      );
    }
  }

  const configs = data?.items ?? [];
  const showForm = creating || editing;

  return (
    <div className="search-config-panel">
      <div className="search-config-header">
        <h3>Search Configs</h3>
        {!showForm && (
          <button className="btn btn-sm" onClick={startCreate}>
            New Config
          </button>
        )}
      </div>

      {isLoading && <div className="status-line">Loading configs...</div>}

      {showForm && (
        <div className="search-config-form">
          <div className="search-config-form-grid">
            <label>
              Name
              <input
                type="text"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
              />
            </label>
            <label>
              Mode
              <select
                value={form.mode}
                onChange={(e) => setForm({ ...form, mode: e.target.value })}
              >
                <option value="subagent">Sub-agent</option>
                <option value="direct">Direct</option>
              </select>
            </label>
            <label>
              Sources
              <select
                value={form.sources}
                onChange={(e) => setForm({ ...form, sources: e.target.value })}
              >
                <option value="reddit">Reddit</option>
                <option value="web">Web</option>
                <option value="reddit,web">Reddit + Web</option>
              </select>
            </label>
            <label>
              Max threads
              <input
                type="number"
                min={1}
                max={20}
                value={form.reddit_max_threads}
                onChange={(e) =>
                  setForm({ ...form, reddit_max_threads: Number(e.target.value) })
                }
              />
            </label>
            <label>
              Max comments
              <input
                type="number"
                min={1}
                max={50}
                value={form.reddit_max_comments}
                onChange={(e) =>
                  setForm({ ...form, reddit_max_comments: Number(e.target.value) })
                }
              />
            </label>
            <label>
              Comment depth
              <input
                type="number"
                min={1}
                max={5}
                value={form.reddit_max_depth}
                onChange={(e) =>
                  setForm({ ...form, reddit_max_depth: Number(e.target.value) })
                }
              />
            </label>
            <label>
              Max web pages
              <input
                type="number"
                min={1}
                max={20}
                value={form.web_max_pages}
                onChange={(e) =>
                  setForm({ ...form, web_max_pages: Number(e.target.value) })
                }
              />
            </label>
          </div>
          <div className="search-config-form-actions">
            <button
              className="btn btn-sm"
              onClick={handleSave}
              disabled={!form.name || createMutation.isPending || updateMutation.isPending}
            >
              {creating ? "Create" : "Save"}
            </button>
            <button className="btn btn-sm btn-secondary" onClick={cancelEdit}>
              Cancel
            </button>
          </div>
        </div>
      )}

      {configs.length > 0 && !showForm && (
        <table className="search-config-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Mode</th>
              <th>Sources</th>
              <th>Params</th>
              <th>Default</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {configs.map((cfg) => (
              <tr key={cfg.id} className={cfg.is_default ? "default-config" : ""}>
                <td>{cfg.name}</td>
                <td>
                  <span className={`search-mode-badge ${cfg.mode}`}>{cfg.mode}</span>
                </td>
                <td>
                  <span className="search-sources-badge">{cfg.sources}</span>
                </td>
                <td className="search-params-cell">
                  {cfg.sources.includes("reddit") && (
                    <span className="search-param">
                      {cfg.reddit_max_threads}t/{cfg.reddit_max_comments}c/{cfg.reddit_max_depth}d
                    </span>
                  )}
                  {cfg.sources.includes("web") && (
                    <span className="search-param">{cfg.web_max_pages}p</span>
                  )}
                </td>
                <td>
                  {cfg.is_default ? (
                    <span className="search-default-badge">Default</span>
                  ) : (
                    <button
                      className="btn btn-xs"
                      onClick={() => setDefaultMutation.mutate(cfg.id)}
                      disabled={setDefaultMutation.isPending}
                    >
                      Set default
                    </button>
                  )}
                </td>
                <td className="search-config-actions">
                  <button className="btn btn-xs" onClick={() => startEdit(cfg)}>
                    Edit
                  </button>
                  <button
                    className="btn btn-xs btn-danger"
                    onClick={() => deleteMutation.mutate(cfg.id)}
                    disabled={deleteMutation.isPending}
                  >
                    Delete
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {configs.length === 0 && !isLoading && !showForm && (
        <div className="status-line">
          No configs yet. The /search skill will use built-in defaults.
        </div>
      )}
    </div>
  );
}

function RunRow({
  run,
  isSelected,
  onSelect,
}: {
  run: SearchRun;
  isSelected: boolean;
  onSelect: () => void;
}) {
  return (
    <tr
      className={`search-run-row ${isSelected ? "selected" : ""}`}
      onClick={onSelect}
    >
      <td className="search-run-query">{run.query}</td>
      <td>
        <span className={`search-mode-badge ${run.mode}`}>{run.mode}</span>
      </td>
      <td>
        <span className="search-sources-badge">{run.sources}</span>
      </td>
      <td>
        <span className={`search-status-badge ${run.status}`}>{run.status}</span>
      </td>
      <td className="search-run-stats">
        {run.stats && (
          <>
            {(run.stats as Record<string, number>).queries_run && (
              <span>{(run.stats as Record<string, number>).queries_run}q</span>
            )}
            {(run.stats as Record<string, number>).threads_fetched && (
              <span>{(run.stats as Record<string, number>).threads_fetched}t</span>
            )}
            {(run.stats as Record<string, number>).pages_fetched && (
              <span>{(run.stats as Record<string, number>).pages_fetched}p</span>
            )}
          </>
        )}
      </td>
      <td className="search-run-date">{formatRelative(run.created_at)}</td>
    </tr>
  );
}

function RunDetail({ runId }: { runId: string }) {
  const { data: run, isLoading, error } = useSearchRunDetail(runId);
  const [showRaw, setShowRaw] = useState(false);

  if (isLoading) return <div className="status-line">Loading run details...</div>;
  if (error || !run) return <div className="status-line error">Failed to load run</div>;

  return (
    <div className="search-run-detail">
      <div className="search-run-detail-header">
        <h4>"{run.query}"</h4>
        <div className="search-run-detail-meta">
          <span className={`search-mode-badge ${run.mode}`}>{run.mode}</span>
          <span className="search-sources-badge">{run.sources}</span>
          <span className={`search-status-badge ${run.status}`}>{run.status}</span>
          <span>{formatRelative(run.created_at)}</span>
        </div>
      </div>

      {run.synthesis_text && (
        <div className="search-synthesis">
          <h5>Synthesis</h5>
          <pre className="search-synthesis-content">{run.synthesis_text}</pre>
        </div>
      )}

      {run.raw_dumps.length > 0 && (
        <div className="search-raw-dumps">
          <button
            className="btn btn-sm"
            onClick={() => setShowRaw(!showRaw)}
          >
            {showRaw ? "Hide" : "Show"} raw data ({run.raw_dumps.length} files)
          </button>
          {showRaw &&
            run.raw_dumps.map((dump) => (
              <details key={dump.id} className="search-raw-dump">
                <summary>{dump.filename}</summary>
                <pre>{dump.content}</pre>
              </details>
            ))}
        </div>
      )}

      {run.stats && (
        <details className="search-run-stats-detail">
          <summary>Stats</summary>
          <pre>{JSON.stringify(run.stats, null, 2)}</pre>
        </details>
      )}
    </div>
  );
}

function SearchHistory() {
  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
  } = useSearchRuns(20);
  const [selectedRunId, setSelectedRunId] = useState<string | null>(null);

  const runs = data?.pages.flatMap((p) => p.items) ?? [];

  return (
    <div className="search-history">
      <h3>Search History</h3>

      {isLoading && <div className="status-line">Loading...</div>}

      {runs.length === 0 && !isLoading && (
        <div className="status-line">No searches yet. Run /search from Claude Code.</div>
      )}

      {runs.length > 0 && (
        <table className="search-runs-table">
          <thead>
            <tr>
              <th>Query</th>
              <th>Mode</th>
              <th>Sources</th>
              <th>Status</th>
              <th>Stats</th>
              <th>When</th>
            </tr>
          </thead>
          <tbody>
            {runs.map((run) => (
              <RunRow
                key={run.id}
                run={run}
                isSelected={selectedRunId === run.id}
                onSelect={() =>
                  setSelectedRunId(selectedRunId === run.id ? null : run.id)
                }
              />
            ))}
          </tbody>
        </table>
      )}

      {hasNextPage && (
        <div className="search-load-more">
          <button
            className="btn btn-sm"
            onClick={() => fetchNextPage()}
            disabled={isFetchingNextPage}
          >
            {isFetchingNextPage ? "Loading..." : "Load older"}
          </button>
        </div>
      )}

      {selectedRunId && <RunDetail runId={selectedRunId} />}
    </div>
  );
}

export function SearchView() {
  return (
    <div className="search-view">
      <ConfigPanel />
      <SearchHistory />
    </div>
  );
}
